import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  computeProcessPrice,
  get3DFile,
  IndustrialProcessSchema,
  OptimizedMachineTimes,
  ProcessStep,
  ProductLine,
  withOptimizedMachineTimes,
} from "shared";

type ProcessStepWithProductLines = ProcessStep & {
  productLines: ProductLine[];
};

const filterProductsAndOutsourcing = <T extends ProcessStepWithProductLines>(processSteps: T[]) =>
  processSteps.filter(
    (processStep) =>
      processStep.industrialProcess !== IndustrialProcessSchema.enum.PRODUCTS &&
      processStep.industrialProcess !== IndustrialProcessSchema.enum.OUTSOURCING,
  );

export const makeQuoteRequestLinesCsv = (
  quoteRequestLines: QuoteRequestLineDto[],
  quoteRequestReference: string | null | undefined,
  optimizedMachineTimes: OptimizedMachineTimes,
) => {
  const separator = ",";
  const csvData = [
    [
      "référence devis",
      "assemblage",
      "reference",
      "quantité",
      "matière",
      "catégorie",
      "description",
      "temps de fabrication",
      "temps de préparation",
      "taux horaire",
      "coût unitaire",
      "url",
    ].join(separator),
  ];

  const rows = quoteRequestLines.flatMap((quoteRequestLine) => {
    return filterProductsAndOutsourcing(quoteRequestLine.processSteps).map((rawProcessStep) => {
      const processStep = withOptimizedMachineTimes(rawProcessStep, optimizedMachineTimes);
      const processName = processStep.industrialProcess;

      const cycleTime = processStep.otherMachineTime || 0;

      const preparationTime = processStep.machinePreparationTime || 0;

      const machineHourlyPrice = processStep.machineHourlyPrice || 0;

      const materialName = quoteRequestLine.materialName || quoteRequestLine.materialGrade;

      const { partProcessCost } = computeProcessPrice({
        quantity: quoteRequestLine.quantity,
        quantityInAssembly: quoteRequestLine.part.quantityInAssembly,
        reworkRatio: quoteRequestLine.reworkRatio,
        processStep,
        optimizedMachineTimes,
      });

      const file3DUrl = get3DFile(quoteRequestLine.part.files)?.url || "";

      return [
        quoteRequestReference || "",
        quoteRequestLine.part.rootParentId || quoteRequestLine.part.id,
        quoteRequestLine.part.name,
        quoteRequestLine.quantity,
        materialName,
        processName,
        processStep.description ?? "",
        cycleTime,
        preparationTime,
        machineHourlyPrice / 100,
        partProcessCost / 100,
        file3DUrl,
      ]
        .map((field) => `"${field ?? "-"}"`)
        .join(separator);
    });
  });

  return csvData.concat(rows);
};
